import type { ReactNode } from 'react';
import { Button, Text, makeStyles, tokens } from '@fluentui/react-components';
import { Add20Regular, DocumentFolder24Regular } from '@fluentui/react-icons';

export interface EmptyAction {
  text: string;
  onClick: () => void;
}

export interface EmptyProps {
  title?: string;
  description?: string;
  icon?: ReactNode;
  action?: EmptyAction;
}

export type EmptyStateConfig = {
  title?: string;
  subtitle?: string;
  icon?: ReactNode;
  action?: EmptyAction;
};

const DEFAULT_TITLE = 'No items yet!';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalL}`,
    textAlign: 'center',
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '48px',
    marginBottom: tokens.spacingVerticalXS,
  },
  title: {
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
  },
  description: {
    color: tokens.colorNeutralForeground3,
    maxWidth: '360px',
    textAlign: 'center',
  },
  action: {
    marginTop: tokens.spacingVerticalS,
  },
});

export const Empty = ({
  title = DEFAULT_TITLE,
  description,
  icon,
  action,
}: EmptyProps) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <div className={styles.icon}>
        {icon ?? <DocumentFolder24Regular style={{ width: 48, height: 48 }} />}
      </div>
      <Text size={400} weight="semibold" className={styles.title}>
        {title}
      </Text>
      {description && (
        <Text size={300} className={styles.description}>
          {description}
        </Text>
      )}
      {action && (
        <Button
          className={styles.action}
          appearance="primary"
          icon={<Add20Regular />}
          onClick={action.onClick}
        >
          {action.text}
        </Button>
      )}
    </div>
  );
};

export default Empty;
